import React from 'react';
import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material';
import { CustomControlImplementation } from '../../../../common/CustomControl';
import { FormField } from '../../../FormField';
import { generateDataTestId } from '../../../../../utils';
import { useDynamicLookup } from '../hooks';
import { LookupOption } from '../types';

const DynamicLookup: React.FC<CustomControlImplementation> = ({
  field,
  fieldValue,
  onFieldValueChange,
  fieldMeta,
  fieldPresentation,
  handleFieldTouch,
  fieldFrame,
}) => {
  const dynamicLookup = fieldMeta?.dynamicLookup;

  const { options, isLoading, parentValue, parentFieldMetadata, lookupParent } =
    useDynamicLookup({
      dynamicLookup,
      onFieldValueChange,
    });

  const { isError, isDisabled, isReadOnly } = fieldFrame;

  // Early return after all hooks have been called
  if (!dynamicLookup) {
    console.warn(
      `DynamicLookup: No dynamicLookup configuration found in fieldMeta for: ${field.Key}`,
    );
    return null;
  }

  // Find current value in options
  const currentOption = fieldValue
    ? options.find((opt) => opt.value === fieldValue) || {
        value: fieldValue,
        label: fieldValue,
      }
    : null;

  const handleChange = (option: LookupOption | null) => {
    const newValue = option?.value || '';
    if (newValue !== fieldValue) {
      onFieldValueChange(newValue);
    }
  };

  const Search = (
    <Autocomplete
      data-testid={generateDataTestId(field, fieldMeta, 'searchField')}
      value={currentOption}
      onChange={(_, newValue) => handleChange(newValue)}
      onClose={handleFieldTouch}
      options={options}
      getOptionLabel={(option) => option.label}
      loading={isLoading}
      disableClearable={false}
      disabled={isDisabled || !parentValue}
      readOnly={isReadOnly}
      renderInput={(params) => (
        <TextField
          {...params}
          error={isError}
          onBlur={handleFieldTouch}
          placeholder={
            !parentValue
              ? `Select ${parentFieldMetadata?.Name || lookupParent} first`
              : `Search ${field.Label || fieldMeta?.Name}`
          }
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          {option.label}
        </Box>
      )}
      isOptionEqualToValue={(option, value) => option.value === value.value}
    />
  );

  return (
    <FormField
      field={field}
      fieldMeta={fieldMeta}
      fieldPresentation={fieldPresentation}
      fieldValue={fieldValue}
      onFieldValueChange={onFieldValueChange}
      {...fieldFrame}
      showRemaining={false}
    >
      {Search}
    </FormField>
  );
};

export default React.memo(DynamicLookup);
